import { handleAxiosError } from "@/api/utils";
import {
  trackSkillArchived,
  trackSkillCreated,
  trackSkillEdited,
  trackSkillPublished,
} from "@/utils/analytics";
import type {
  CreateSkillRequest,
  PresignedSkillFileUpload,
  PresignFileUploadRequest,
  PutSkillRequest,
  Skill,
  SkillFile,
  SkillTemplate,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

export type {
  PresignedSkillFileUpload,
  PresignFileUploadRequest,
  SkillFile,
} from "@cohesive/api-client";

export function useSkills(
  options?: Partial<UseQueryOptions<Skill[], AxiosError>>,
) {
  return useQuery<Skill[], AxiosError>({
    queryKey: ["skills"],
    queryFn: async () => {
      return await orgAPI.skills.list();
    },
    ...options,
  });
}

export function useSkillById(
  skillId: string,
  options?: Partial<UseQueryOptions<Skill, AxiosError>>,
) {
  return useQuery<Skill, AxiosError>({
    queryKey: ["skill", skillId],
    queryFn: async () => {
      return await orgAPI.skills.getById(skillId);
    },
    ...options,
  });
}

export function useCreateSkill(
  options?: UseMutationOptions<Skill, AxiosError, CreateSkillRequest>,
) {
  const queryClient = useQueryClient();
  return useMutation<Skill, AxiosError, CreateSkillRequest>({
    mutationFn: async (data) => {
      return await orgAPI.skills.create(data);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      trackSkillCreated({
        skillId: data.id,
        source: variables.skill_md ? "zip" : "scratch",
      });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function usePutSkill(
  options?: UseMutationOptions<
    Skill,
    AxiosError,
    { id: string; data: PutSkillRequest }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<Skill, AxiosError, { id: string; data: PutSkillRequest }>({
    mutationFn: async ({ id, data }) => {
      return await orgAPI.skills.update(id, data);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      queryClient.invalidateQueries({ queryKey: ["skill", variables.id] });
      // File URLs are version-pinned and prefix-keyed: a description update
      // rewrites SKILL.md server-side, and a status toggle moves every file
      // to another prefix — cached file queries go stale either way.
      queryClient.invalidateQueries({
        queryKey: ["skill-files", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["skill-file", variables.id] });
      trackSkillEdited({ skillId: variables.id });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

/** The DELETE route archives (moves the skill to Trash); restore brings it back. */
export function useDeleteSkill(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: async (skillId) => {
      try {
        await orgAPI.skills.delete(skillId);
      } catch (e) {
        return handleAxiosError(e);
      }
    },
    ...options,
    onSuccess: (_, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      queryClient.invalidateQueries({ queryKey: ["archived"] });
      queryClient.removeQueries({ queryKey: ["skill", variables] });
      trackSkillArchived({ skillId: variables });
      if (options?.onSuccess) {
        options.onSuccess(_, variables, context);
      }
    },
  });
}

/** Publishes a private skill as an organization template. */
export function usePublishSkill(
  options?: UseMutationOptions<SkillTemplate, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<SkillTemplate, AxiosError, string>({
    mutationFn: async (skillId) => {
      return await orgAPI.skills.publish(skillId);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["skill-templates"] });
      trackSkillPublished({ skillId: variables });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useSkillFiles(
  skillId: string,
  options?: Partial<UseQueryOptions<SkillFile[], AxiosError>>,
) {
  return useQuery<SkillFile[], AxiosError>({
    queryKey: ["skill-files", skillId],
    queryFn: async () => {
      return await orgAPI.skills.listFiles(skillId);
    },
    enabled: !!skillId,
    ...options,
  });
}

/** Presigned download URL + metadata for one path (GET .../skills/:id/files/*path). */
export function useSkillFile(
  skillId: string,
  filePath: string | undefined,
  options?: Partial<UseQueryOptions<SkillFile, AxiosError>>,
) {
  return useQuery<SkillFile, AxiosError>({
    queryKey: ["skill-file", skillId, filePath],
    queryFn: async () => {
      return await orgAPI.skills.getFile(skillId, filePath!);
    },
    enabled: !!skillId && !!filePath,
    // The returned URL is pinned to a specific S3 object version, so a cached
    // entry keeps serving pre-save content — always refetch on mount (defense
    // in depth on top of the save-path invalidations).
    refetchOnMount: "always",
    ...options,
  });
}

export function useDeleteSkillFile(
  options?: UseMutationOptions<
    void,
    AxiosError,
    { skillId: string; filePath: string }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { skillId: string; filePath: string }>({
    mutationFn: async ({ skillId, filePath }) => {
      try {
        await orgAPI.skills.deleteFile(skillId, filePath);
      } catch (e) {
        handleAxiosError(e);
      }
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["skill-files", variables.skillId],
      });
      queryClient.removeQueries({
        queryKey: ["skill-file", variables.skillId, variables.filePath],
      });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function usePresignSkillFileUpload(
  options?: UseMutationOptions<
    PresignedSkillFileUpload,
    AxiosError,
    { skillId: string; data: PresignFileUploadRequest }
  >,
) {
  return useMutation({
    mutationFn: async ({
      skillId,
      data,
    }: {
      skillId: string;
      data: PresignFileUploadRequest;
    }) => {
      return await orgAPI.skills.presignFileUpload(skillId, data);
    },
    ...options,
  });
}

export function useUserSkills(params?: { search?: string }) {
  return useQuery({
    queryKey: ["skills", "user", { search: params?.search }],
    queryFn: async (): Promise<Skill[]> => {
      const skills = await orgAPI.skills.list();

      if (params?.search) {
        const searchLower = params.search.toLowerCase();
        return skills.filter(
          (skill) =>
            skill.name.toLowerCase().includes(searchLower) ||
            skill.description?.toLowerCase().includes(searchLower),
        );
      }

      return skills;
    },
    staleTime: 5 * 60 * 1000,
  });
}
